import { RowCrudStatus } from "../../common/types";
import {
  ContractObservation,
  ContractObservationCreateMutationVariables,
  ContractObservationUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { authorComposerDisplayId } from "../../authorComposer/store/AuthorComposerCommonStore";
import { publisherDisplayId } from "../../publisher/store/PublisherCommonStore";

export type ContractObservationValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  title: string;
  observation: string;
  requireAll: boolean;
  __authors: string[];
  showAuthors: { displayId: string; id: string }[];
  __publishers: string[];
  showPublishers: { displayId: string; id: string }[];
};

export type ContractObservationErrorsType = {
  title?: { message: string; fieldLabel: string };
  observation?: { message: string; fieldLabel: string };
  requireAll?: { message: string; fieldLabel: string };
  authors?: { message: string; fieldLabel: string };
  publishers?: { message: string; fieldLabel: string };
};

export function ContractObservationValuesInit(): ContractObservationValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    title: "",
    observation: "",
    requireAll: false,
    __authors: [],
    showAuthors: [],
    __publishers: [],
    showPublishers: [],
  };
}

export type ContractObservationType =
  | ContractObservation
  | ContractObservationValuesType;

export function contractObservationDisplayId(
  source: Partial<ContractObservation>,
  _langStore: LangStore
) {
  return source.title ?? _langStore.emptyStringSymbol;
}

export function getContractObservationValues(
  source: ContractObservationType,
  _langStore: LangStore,
  _duplicated: boolean = false
): ContractObservationValuesType {
  const entityValues = ContractObservationValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : contractObservationDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.title = source.title;
  entityValues.observation = source.observation;
  entityValues.requireAll = source.requireAll || false;
  entityValues.showAuthors =
    "authors" in source && source.authors
      ? (source.authors || []).map((x) => {
          return {
            id: x.id,
            displayId: authorComposerDisplayId(x, _langStore),
          };
        })
      : "showAuthors" in source && source.showAuthors
      ? (source.showAuthors || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__authors = entityValues.showAuthors.map((x) => x.displayId);
  entityValues.showPublishers =
    "publishers" in source && source.publishers
      ? (source.publishers || []).map((x) => {
          return { id: x.id, displayId: publisherDisplayId(x, _langStore) };
        })
      : "showPublishers" in source && source.showPublishers
      ? (source.showPublishers || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__publishers = entityValues.showPublishers.map(
    (x) => x.displayId
  );
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getContractObservationUpdateVariables(
  values: ContractObservationValuesType
): ContractObservationUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    title: values.title,
    observation: values.observation,
    requireAll: values.requireAll === undefined ? null : values.requireAll,
    authorsIds: values.showAuthors
      ? { set: values.showAuthors.map((x) => x.id) }
      : undefined,
    publishersIds: values.showPublishers
      ? { set: values.showPublishers.map((x) => x.id) }
      : undefined,
  };
}

export function getContractObservationCreateVariables(
  values: ContractObservationValuesType
): ContractObservationCreateMutationVariables {
  return {
    title: values.title,
    observation: values.observation,
    requireAll: values.requireAll === undefined ? null : values.requireAll,
    authorsIds: values.showAuthors
      ? values.showAuthors.map((x) => x.id)
      : undefined,
    publishersIds: values.showPublishers
      ? values.showPublishers.map((x) => x.id)
      : undefined,
  };
}
